import React, { useState, useRef, useEffect } from 'react';

import { CloseButton, Input, BaseButton, useT } from '@repo/common';

const DESC_MAX = 500;

const MobileKeyAchievementBottomSheet = ({ initialData, onClose, onSave }) => {
  const t = useT();
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(
    initialData?.description || '',
  );
  const [error, setError] = useState(false);

  const titleRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => titleRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleAdd = () => {
    if (!title.trim()) {
      setError(true);
      return;
    }
    onSave({ title: title.trim(), description: description.trim() });
  };

  return (
    <div className='fixed inset-0 z-[70] flex flex-col justify-end'>
      <div className='absolute inset-0 bg-surface-black40' onClick={onClose} />

      <div className='relative flex flex-col min-h-[530px] pt-4 px-6 pb-10 rounded-t-[40px] bg-grayscale-gray00 gap-6'>
        <div className='shrink-0 pb-3'>
          <CloseButton onClick={onClose} type='light' size='size-[32px]' />
        </div>
        <h2 className='shrink-0 text-[24px] font-bold leading-tight text-text-primary'>
          {t('Key Achievement', '핵심 성과')}
        </h2>

        <div className='flex-1 overflow-y-auto flex flex-col gap-4'>
          {/* Achievement title */}
          <div className='flex flex-col gap-2'>
            <div>
              <div className='flex items-center gap-0.5'>
                <p className='typo-b14Strong text-text-primary'>
                  {t('Achievement title', '성과 제목')}
                </p>
                <span className='typo-b16Strong text-text-accent'>*</span>
              </div>
              <p className='typo-b12 text-text-placeholder'>
                {t(
                  'Quantify your achievements where possible (Outcome-focused, measurable result)',
                  '가능하다면 성과를 수치로 표현해 주세요 (결과 중심의 측정 가능한 성과)',
                )}
              </p>
            </div>
            <Input
              ref={titleRef}
              as={'textarea'}
              label=''
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (error) setError(false);
              }}
              placeholder={t(
                'e.g. Increased user engagement by 12%',
                '예: 사용자 참여도 12% 증가',
              )}
              rows={3}
              state={error && 'failed'}
              message={error && t('Please add a title', '제목을 입력해 주세요')}
            />
          </div>

          {/* Description */}
          <div className='flex flex-col gap-2 pb-5'>
            <div>
              <p className='typo-b14Strong text-text-primary'>
                {t('Description (optional)', '설명 (선택 사항)')}
              </p>
              <p className='typo-b12 text-text-placeholder'>
                {t(
                  'Explain how you achieved this result (What you did + how you contributed: tools, actions, collaboration)',
                  '이 결과를 어떻게 달성했는지 설명해 주세요 (수행한 일과 기여한 방식: 도구, 행동, 협업)',
                )}
              </p>
            </div>
            <Input
              as={'textarea'}
              label=''
              value={description}
              onChange={(e) =>
                setDescription(e.target.value.slice(0, DESC_MAX))
              }
              placeholder={t(
                'e.g. Designed visual UI components for the product detail page, focusing on visual hierarchy, layout balance, and clarity.',
                '예: 제품 상세 페이지의 시각적 UI 컴포넌트를 디자인하고, 시각적 계층 구조와 레이아웃 균형 및 명확성에 집중했습니다.',
              )}
              rows={8}
              maxLength={DESC_MAX}
            />
          </div>
        </div>
      </div>

      <div className='relative shrink-0 p-4 bg-grayscale-gray00'>
        <BaseButton onClick={handleAdd} size='l' hideIcon width='100%'>
          {t('Add Key Achievement', '핵심 성과 추가')}
        </BaseButton>
      </div>
    </div>
  );
};

export default MobileKeyAchievementBottomSheet;
